/**
 * DispatchPeek (FLOW-01, Seth 2026-06-10): clicking a dispatch from the
 * customer page should NOT leave the customer page. The dispatcher's working
 * window stays on the 360 (two-window model — details expand in place); the
 * dispatch detail slides in from the right with everything mid-call matters:
 * status, window, tech, address, phone, ESC notes, and the Pegasus note
 * editor. "Open full dispatch" is the deliberate drill-down for the rare
 * deeper dig (invoices, sell-an-install, multi-tech surgery).
 *
 * Reuses the single-dispatch fetch (dispatch-detail/api) and the board's
 * Pegasus-note hooks — one data path, one note store, three surfaces.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Clock, History, MapPin, Phone as PhoneIcon, Receipt, User, Wrench } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { SlideOver } from "@/components/ui/SlideOver";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { WindowBadge } from "@/components/ui/WindowBadge";
import { TechLink } from "@/components/links";
import {
  displayCustomerName,
  formatTime,
  formatDuration,
  resolveWindowAndPriority,
} from "@/lib/format";
import { format, parseISO } from "date-fns";
import { useDispatch, groupTechAssignments } from "@/features/dispatch-detail/api";
import { useDispatchNote, useSaveDispatchNote } from "@/features/board/api";
import { DispatchFiles } from "@/features/files/DispatchFiles";
import { Copyable, CopyCode, mailingLabel } from "@/components/Copyable";
import { useVisitEquipment, visitEquipmentFor } from "./visitEquipment";
import { VisitEquipmentBlock } from "./VisitEquipmentBlock";

export function DispatchPeek({
  dispatchId,
  onClose,
  historyJump = true,
}: {
  dispatchId: string | null;
  onClose: () => void;
  /** The History tab peeks its own rows — its jump button would only point
   *  back at itself, so it passes false. */
  historyJump?: boolean;
}) {
  const navigate = useNavigate();
  const { data: d, isLoading, isError } = useDispatch(dispatchId ?? undefined);
  // STRIP-QUICKJUMP (Seth 2026-08-21: "maybe an option to also quickly go to
  // the invoices or history"): the visit's linked invoice(s) via
  // mirror_sales.dispatch (91% of invoices carry the link) + a History jump.
  const { data: linkedInvoices } = useQuery({
    queryKey: ["dispatch_invoices", dispatchId],
    enabled: !!dispatchId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data: rows, error } = await supabase
        .from("mirror_sales").select("invoice").eq("dispatch", dispatchId as string).limit(50);
      if (error) throw error;
      return [...new Set((rows ?? []).map((r) => String((r as { invoice: string | number }).invoice)))];
    },
  });

  // HIST-EQUIP-01: the unit(s) this visit installed / worked on.
  const { data: visitEq } = useVisitEquipment(d?.custno ?? null);
  // Pegasus-native note (same store the board + cockpit edit).
  const { data: pegNote } = useDispatchNote(dispatchId ?? "");
  const saveNote = useSaveDispatchNote();
  const savedNote = pegNote?.note ?? "";
  const [draft, setDraft] = useState(savedNote);
  useEffect(() => {
    setDraft(pegNote?.note ?? "");
  }, [dispatchId, pegNote?.note]);
  const dirty = draft !== savedNote;

  const customer = d
    ? displayCustomerName(d.locname, d.customer_fullname)
    : "";
  const wp = d ? resolveWindowAndPriority(d.window_code, d.notes) : null;
  let dateLabel = d?.appointment_date ?? "";
  try {
    if (d?.appointment_date)
      dateLabel = format(parseISO(d.appointment_date), "EEE, MMM d, yyyy");
  } catch {
    /* keep raw */
  }

  return (
    <SlideOver
      open={!!dispatchId}
      onClose={onClose}
      title={d ? `Dispatch ${d.dispatch}` : "Dispatch"}
      subtitle={d ? `${customer} · ${dateLabel}` : undefined}
      size="md"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (dispatchId) navigate(`/dispatch/${dispatchId}?from=customer`);
              }}
            >
              Open full dispatch
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
            {(linkedInvoices ?? []).slice(0, 2).map((inv) => (
              <Button key={inv} variant="secondary" size="sm" onClick={() => navigate(`/invoice/${inv}`)}>
                <Receipt className="h-3.5 w-3.5" /> Invoice #{inv.replace(/^0+/, "")}
              </Button>
            ))}
            {d && historyJump && (
              // Lands on THIS visit in the timeline (History reads ?visit=).
              <Button variant="secondary" size="sm" onClick={() => { onClose(); navigate(`/customers/${d.custno}?tab=history&visit=${encodeURIComponent(d.dispatch)}`); }}>
                <History className="h-3.5 w-3.5" /> History
              </Button>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {isError ? (
        /* 360-AUDIT #18: an error looked like eternal skeletons. */
        <p className="rounded-md bg-rose-50/60 px-3 py-2.5 text-sm font-medium text-rose-700">
          Couldn&apos;t load this — close and reopen to retry.
        </p>
      ) : isLoading || !d ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Status + window + time pills */}
          <div className="flex flex-wrap items-center gap-2">
            {/* COPY-01: the call number, one click to copy. */}
            <CopyCode value={d.dispatch} label="Copy call number" size="md">#{d.dispatch}</CopyCode>
            {d.techs[0]?.status && <StatusBadge status={d.techs[0].status} />}
            {wp?.timeWindow && <WindowBadge code={wp.timeWindow} />}
            {wp?.priority && <WindowBadge code={wp.priority} />}
            {formatTime(d.tpromtime) && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <Clock className="h-3 w-3" />
                {formatTime(d.tpromtime)}
              </span>
            )}
            {(d.techs[0]?.tech_est_hours || d.dispatch_est_min) && (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                est {formatDuration(d.techs[0]?.tech_est_hours ?? d.dispatch_est_min)}
              </span>
            )}
          </div>

          {/* Tech(s) — one line per TECH; multi-day installs roll up to
              "N days · span" instead of repeating the name (Seth 2026-06-11). */}
          {(() => {
            const groups = groupTechAssignments(d.techs);
            return (
              <PeekSection icon={User} label={groups.length > 1 ? "Assigned techs" : "Assigned tech"}>
                <div className="space-y-0.5">
                  {groups.map((g) => (
                    <div key={g.tech_empno} className="text-sm">
                      <TechLink empno={g.tech_empno} name={g.name} />
                      <span className="ml-2 text-xs text-slate-400">
                        {g.days.length > 1
                          ? `${g.days.length} days on the job`
                          : g.latestStatus ?? ""}
                      </span>
                    </div>
                  ))}
                </div>
              </PeekSection>
            );
          })()}

          {/* Address + phone */}
          {(d.service_address || d.city) && (
            <PeekSection icon={MapPin} label="Service address">
              <Copyable
                text={mailingLabel({
                  name: customer,
                  address: d.service_address,
                  city: d.city,
                  state: d.state,
                  zip: d.zip,
                })}
                label="Copy name + address"
              >
                <div className="text-sm text-slate-800">
                  {d.service_address}
                  {d.city && (
                    <>
                      <br />
                      {d.city}, {d.state} {d.zip}
                    </>
                  )}
                </div>
              </Copyable>
            </PeekSection>
          )}
          {d.customer_phone && (
            <PeekSection icon={PhoneIcon} label="Customer phone">
              <Copyable text={d.customer_phone} label="Copy phone">
                <a
                  href={`tel:${d.customer_phone.replace(/[^0-9+]/g, "")}`}
                  className="text-sm text-brand-700 hover:underline"
                >
                  {d.customer_phone}
                </a>
              </Copyable>
            </PeekSection>
          )}

          {/* HIST-EQUIP-01 — what this visit installed / worked on. */}
          <PeekSection icon={Wrench} label="Equipment">
            <VisitEquipmentBlock
              source="esc"
              equipment={visitEquipmentFor(visitEq, [d.dispatch, ...(linkedInvoices ?? [])])}
            />
          </PeekSection>

          {/* Files pinned to this dispatch (FILES-03) — permits, install
              forms, job-site photos; most of the ESC import lives here. */}
          <DispatchFiles dispatch={d.dispatch} custno={d.custno} locno={d.locno} />

          {/* Notes — ESC read-only + the Pegasus editor (same store as board) */}
          {d.notes && (
            <PeekSection label="ESC notes (read-only)">
              <p className="whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm leading-relaxed text-slate-800">
                {d.notes}
              </p>
            </PeekSection>
          )}
          <PeekSection label="Pegasus notes (internal)">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              placeholder="Add an internal note for this dispatch…"
              className="w-full resize-none rounded-md border border-hairline bg-white p-2 text-sm text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
            <div className="mt-1.5 flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                loading={saveNote.isPending}
                disabled={!dirty}
                onClick={() =>
                  saveNote.mutate({
                    dispatch: d.dispatch,
                    custno: d.custno,
                    locno: d.locno,
                    note: draft,
                  })
                }
              >
                Save note
              </Button>
              {!dirty && savedNote && (
                <span className="text-xs text-slate-400">
                  Saved{pegNote?.updated_by ? ` · ${pegNote.updated_by}` : ""}
                </span>
              )}
              {saveNote.isError && (
                <span className="text-xs text-red-600">Couldn&apos;t save</span>
              )}
            </div>
          </PeekSection>
        </div>
      )}
    </SlideOver>
  );
}

function PeekSection({
  icon: Icon,
  label,
  children,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      {/* HD (2026-09-01): real 13px titles, not whisper-caps. */}
      <div className="mb-1.5 flex items-center gap-1.5 text-[13px] font-bold text-slate-700">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
        {label}
      </div>
      {children}
    </div>
  );
}
